/**
 * Splits server copy that uses `**x**` markers into plain / highlighted
 * runs. Used by <HighlightedText> to paint the marked phrases in the
 * accent green ("Get **$20**." → "Get ", ["$20"], ".").
 *
 * Only the double-asterisk marker is honored — no other markdown. An
 * unclosed `**` is treated as literal text so a typo can't swallow the
 * rest of the sentence.
 */
export interface HighlightRun {
  text: string;
  highlighted: boolean;
}

const MARKER = /\*\*([\s\S]+?)\*\*/g;

export function parseHighlightMarkup(input: string): HighlightRun[] {
  const runs: HighlightRun[] = [];
  let last = 0;
  for (const match of input.matchAll(MARKER)) {
    const start = match.index;
    if (start > last) runs.push({ text: input.slice(last, start), highlighted: false });
    runs.push({ text: match[1] ?? '', highlighted: true });
    last = start + match[0].length;
  }
  if (last < input.length) runs.push({ text: input.slice(last), highlighted: false });
  return runs;
}

/** Plain-text form (markers stripped) — for accessibility labels and tests. */
export function stripHighlightMarkup(input: string): string {
  return input.replace(MARKER, '$1');
}

/**
 * Marks the first dollar amount in a headline ("Get up to $3,000 with…" →
 * "Get up to **$3,000** with…") so the reward reads in accent green. Copy
 * that already carries markers is left as authored.
 */
export function markFirstAmount(input: string): string {
  if (input.includes('**')) return input;
  // Thousands groups only, so a trailing comma ("$50, then…") stays plain.
  return input.replace(/\$\d{1,3}(?:,\d{3})+(?:\.\d+)?|\$\d+(?:\.\d+)?/, (amount) => `**${amount}**`);
}
